import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="descripcion-imagen"
export default class extends Controller {
  static targets = [
    "hiddenFields",
    "modalityField",
    "modalityFilters",
    "resultsEmptyState",
    "resultsList",
    "searchInput",
    "selectedCount",
    "selectedEmptyState",
    "selectedList",
    "weightField",
    "heightField"
  ]

  static values = {
    laterabilidadOptions: Array,
    modalities: Array,
    proyeccionOptions: Array,
    readonly: Boolean,
    selectedPractices: Array,
    url: String,
    descriptionsParam: { type: String, default: "pedido_de_imagen[pedido_de_imagen_descripciones_attributes]" }
  }

  connect() {
    this.activeModality = this.initialModality()
    this.searchTimeout = null
    this.selectedPractices = this.normalizePractices(this.selectedPracticesValue)

    this.render()
    this.search()
    this.notifyPracticesChanged()
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  queueSearch() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    this.searchTimeout = setTimeout(() => this.search(), 200)
  }

  setModality(event) {
    if (this.readonlyValue) return

    if (this.selectedPractices.length > 0) {
      return
    }

    this.activeModality = event.currentTarget.dataset.modality || ""
    this.renderFilters()
    this.search()

    if (this.hasModalityFieldTarget) {
      this.modalityFieldTarget.value = this.activeModality
      this.notifyPracticesChanged()
    }
  }

  addPractice(event) {
    if (this.readonlyValue) return

    const payload = JSON.parse(event.currentTarget.dataset.practice)

    if (this.selectedPractices.some(practice => practice.id === payload.id)) {
      return
    }

    this.selectedPractices.push({
      id: payload.id,
      study: payload.study,
      modality: payload.modality,
      tipo_exposicion: "",
      ubicacion: "",
      prioridad: ""
    })

    if (!this.activeModality) {
      this.activeModality = payload.modality || ""
    }

    this.render()
    this.search()
    this.notifyPracticesChanged()
  }

  removePractice(event) {
    if (this.readonlyValue) return

    const practiceId = Number(event.currentTarget.dataset.practiceId)
    this.selectedPractices = this.selectedPractices.filter(practice => practice.id !== practiceId)
    this.render()
    this.search()
    this.notifyPracticesChanged()
  }

  updatePracticeField(event) {
    const index = Number(event.currentTarget.dataset.index)
    const field = event.currentTarget.dataset.field

    if (!Number.isInteger(index) || !field || !this.selectedPractices[index]) {
      return
    }

    this.selectedPractices[index][field] = event.currentTarget.value
    this.renderHiddenFields()
  }

  async search() {
    const params = new URLSearchParams()
    const query = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
    const modality = this.currentModality()

    if (query) {
      params.set("q", query)
    }

    if (modality) {
      params.set("modality", modality)
    }

    const response = await fetch(`${this.urlValue}?${params.toString()}`)
    const results = response.ok ? await response.json() : []

    this.renderResults(results)
  }

  render() {
    this.renderFilters()
    this.renderSelectedPractices()
    this.renderHiddenFields()
    this.syncModalityField()
  }

  renderFilters() {
    const selectedModality = this.selectedPractices[0]?.modality
    const filters = [{ label: "Todas", value: "" }]
      .concat(this.modalitiesValue.map(modality => ({ label: modality, value: modality })))

    this.modalityFiltersTarget.innerHTML = filters.map(filter => {
      const activeClass = filter.value === this.activeModality ? "btn-primary" : "btn-outline-secondary"
      const disabled = this.readonlyValue || (selectedModality && filter.value !== selectedModality)
      const disabledAttr = disabled ? "disabled" : ""

      return `
        <button
          type="button"
          class="btn btn-sm ${activeClass}"
          data-action="click->descripcion-imagen#setModality"
          data-modality="${this.escapeAttribute(filter.value)}"
          ${disabledAttr}
        >
          ${this.escapeHtml(filter.label)}
        </button>
      `
    }).join("")
  }

  renderSelectedPractices() {
    this.selectedCountTarget.textContent = this.selectedPractices.length
    this.selectedEmptyStateTarget.classList.toggle("d-none", this.selectedPractices.length > 0)

    this.selectedListTarget.innerHTML = this.selectedPractices.map((practice, index) => {
      const removeButton = this.readonlyValue ? "" : `
        <button
          type="button"
          class="btn btn-sm btn-outline-danger"
          data-action="click->descripcion-imagen#removePractice"
          data-practice-id="${practice.id}"
        >
          Quitar
        </button>
      `

      return `
        <div class="border rounded p-3 mb-3 bg-white">
          <div class="d-flex justify-content-between align-items-start mb-3" style="gap: 1rem;">
            <div>
              <div class="font-weight-bold">${this.escapeHtml(practice.study || "Práctica sin nombre")}</div>
              <small class="text-muted">${this.escapeHtml(practice.modality || "")}</small>
            </div>
            ${removeButton}
          </div>
          <div class="form-row">
            <div class="col-md-4 mb-3">
              <label class="small text-uppercase text-muted mb-1">Laterabilidad</label>
              ${this.selectMarkup({
                field: "ubicacion",
                index,
                value: practice.ubicacion,
                options: this.laterabilidadOptionsValue,
                placeholder: "Seleccionar..."
              })}
            </div>
            <div class="col-md-4 mb-3">
              <label class="small text-uppercase text-muted mb-1">Proyeccion</label>
              ${this.selectMarkup({
                field: "tipo_exposicion",
                index,
                value: practice.tipo_exposicion,
                options: this.proyeccionOptionsValue,
                placeholder: "Seleccionar..."
              })}
            </div>
            <div class="col-md-4 mb-3">
              <label class="small text-uppercase text-muted mb-1">Detalle</label>
              ${this.textInputMarkup({
                field: "prioridad",
                index,
                value: practice.prioridad || "",
                placeholder: "Detalle"
              })}
            </div>
          </div>
        </div>
      `
    }).join("")
  }

  renderResults(results) {
    const selectedIds = new Set(this.selectedPractices.map(practice => practice.id))

    this.resultsListTarget.innerHTML = results.map(item => {
      const selected = selectedIds.has(item.id)
      const canAdd = !selected && !this.readonlyValue

      return `
        <div class="border rounded px-3 py-2 mb-2 ${selected ? "bg-light" : "bg-white"}">
          <div class="d-flex justify-content-between align-items-center" style="gap: 1rem;">
            <div>
              <div class="font-weight-bold">${this.escapeHtml(item.study)}</div>
              <small class="text-muted">${this.escapeHtml(item.modality || "")}</small>
            </div>
            ${selected ? '<span class="text-muted small">Agregada</span>' : canAdd ? `
              <button
                type="button"
                class="btn btn-sm btn-link p-0"
                data-action="click->descripcion-imagen#addPractice"
                data-practice='${this.escapeAttribute(JSON.stringify(item))}'
                ${this.readonlyValue ? "disabled" : ""}
              >
                + Agregar
              </button>
            ` : ''}
          </div>
        </div>
      `
    }).join("")

    this.resultsEmptyStateTarget.classList.toggle("d-none", results.length > 0)
  }

  renderHiddenFields() {
    const prefix = this.descriptionsParamValue
    const isTratamiento = prefix.includes("turno_tratamiento_previo")

    this.hiddenFieldsTarget.innerHTML = this.selectedPractices.map((practice, index) => {
      if (isTratamiento) {
        return `
          <input type="hidden" name="${prefix}[${index}][descripcion_imagen_id]" value="${practice.id}">
          <input type="hidden" name="${prefix}[${index}][cantidad]" value="1">
        `
      }

      return `
        <input type="hidden" name="${prefix}[${index}][descripcion_imagen_id]" value="${practice.id}">
        <input type="hidden" name="${prefix}[${index}][tipo_exposicion]" value="${this.escapeAttribute(practice.tipo_exposicion || "")}">
        <input type="hidden" name="${prefix}[${index}][ubicacion]" value="${this.escapeAttribute(practice.ubicacion || "")}">
        <input type="hidden" name="${prefix}[${index}][prioridad]" value="${this.escapeAttribute(practice.prioridad || "")}">
      `
    }).join("")
  }

  syncModalityField() {
    if (!this.hasModalityFieldTarget) return

    const modality = this.selectedPractices[0]?.modality || this.activeModality || ""
    this.modalityFieldTarget.value = modality

    const requiresWeightHeight = modality.toLowerCase().includes("tomografia")

    if (this.hasWeightFieldTarget) {
      this.weightFieldTarget.required = requiresWeightHeight
      const label = this.weightFieldTarget.closest(".form-group")?.querySelector("label")
      if (label) label.textContent = requiresWeightHeight ? "Peso *" : "Peso"
    }

    if (this.hasHeightFieldTarget) {
      this.heightFieldTarget.required = requiresWeightHeight
      const label = this.heightFieldTarget.closest(".form-group")?.querySelector("label")
      if (label) label.textContent = requiresWeightHeight ? "Talla *" : "Talla"
    }

    this.notifyPracticesChanged()
  }

  selectMarkup({ field, index, value, options, placeholder }) {
    const optionTags = []
    const normalizedOptions = this.normalizeOptionsWithCurrentValue(options, value)

    if (placeholder !== null) {
      optionTags.push(`<option value="">${this.escapeHtml(placeholder)}</option>`)
    }

    normalizedOptions.forEach(([label, optionValue]) => {
      const selected = optionValue === value ? "selected" : ""
      optionTags.push(`<option value="${this.escapeAttribute(optionValue)}" ${selected}>${this.escapeHtml(label)}</option>`)
    })

    return `
      <select
        class="form-control form-control-sm"
        data-action="change->descripcion-imagen#updatePracticeField"
        data-index="${index}"
        data-field="${field}"
        ${this.readonlyValue ? "disabled" : ""}
      >
        ${optionTags.join("")}
      </select>
    `
  }

  textInputMarkup({ field, index, value, placeholder }) {
    return `
      <input
        type="text"
        class="form-control form-control-sm"
        value="${this.escapeAttribute(value || "")}"
        placeholder="${this.escapeAttribute(placeholder || "")}"
        data-action="input->descripcion-imagen#updatePracticeField"
        data-index="${index}"
        data-field="${field}"
        ${this.readonlyValue ? "readonly" : ""}
      >
    `
  }

  normalizeOptionsWithCurrentValue(options, currentValue) {
    const normalizedOptions = Array.isArray(options) ? [...options] : []
    const value = String(currentValue || "").trim()
    if (!value) return normalizedOptions

    const exists = normalizedOptions.some(([, optionValue]) => String(optionValue) === value)
    if (exists) return normalizedOptions

    normalizedOptions.push([this.humanizeLegacyValue(value), value])
    return normalizedOptions
  }

  humanizeLegacyValue(value) {
    return value
      .replaceAll("_", " ")
      .split(" ")
      .map(token => token ? `${token.charAt(0).toUpperCase()}${token.slice(1)}` : "")
      .join(" ")
  }

  initialModality() {
    if (this.hasModalityFieldTarget && this.modalityFieldTarget.value) {
      return this.modalityFieldTarget.value
    }

    return this.selectedPracticesValue[0]?.modality || ""
  }

  currentModality() {
    return this.selectedPractices[0]?.modality || this.activeModality || ""
  }

  normalizePractices(practices) {
    return practices.map(practice => ({
      ...practice,
      id: Number(practice.id),
      prioridad: practice.prioridad || "",
      tipo_exposicion: practice.tipo_exposicion || "",
      ubicacion: practice.ubicacion || ""
    }))
  }

  escapeHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replaceAll("`", "&#96;")
  }

  notifyPracticesChanged() {
    this.dispatch("practices-changed", { detail: { count: this.selectedPractices.length } })
  }
}
